import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminUsers, unlockUserAccount, resetUserPassword, startReadOnlySession, upgradeUserPlan, downgradeUserPlan, deleteUser, updateUserRole } from '../utils/api';
import { useReadOnly } from '../context/ReadOnlyContext';
import { toast } from 'sonner';
import { Users, Lock, Key, Search, Filter, ChevronLeft, ChevronRight, Shield, AlertTriangle, Eye, Sparkles, ArrowDown, Trash2, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';

const AdminPage = () => {
  const navigate = useNavigate();
  const { startReadOnlySession: startSession } = useReadOnly();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [sortBy, setSortBy] = useState('last_login');
  
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    type: null,
    user: null
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 50,
        search: search || undefined,
        country: countryFilter || undefined,
        sort_by: sortBy
      };
      
      const response = await getAdminUsers(params);
      setUsers(response.users);
      setTotalPages(response.total_pages);
      setTotal(response.total);
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, search, countryFilter, sortBy]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleUnlockAccount = async (user) => {
    try {
      await unlockUserAccount(user.id);
      toast.success(`Account unlocked for ${user.email}`);
      fetchUsers();
      setConfirmDialog({ show: false, type: null, user: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock account');
    }
  };

  const handleResetPassword = async (user) => {
    try {
      await resetUserPassword(user.id);
      toast.success(`Password reset email sent to ${user.email}`);
      setConfirmDialog({ show: false, type: null, user: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    }
  };

  const handleViewAsUser = async (user) => {
    try {
      const response = await startReadOnlySession(user.id);
      startSession(response.readonly_token, response.target_user);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start read-only session');
    }
  };

  const handleUpgradePlan = async (user) => {
    try {
      await upgradeUserPlan(user.id);
      toast.success(`${user.email} upgraded to full access`);
      fetchUsers();
      setConfirmDialog({ show: false, type: null, user: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upgrade user');
    }
  };

  const handleDowngradePlan = async (user) => {
    try {
      await downgradeUserPlan(user.id);
      toast.success(`${user.email} downgraded to free plan`);
      fetchUsers();
      setConfirmDialog({ show: false, type: null, user: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to downgrade user');
    }
  };

  const handleToggleAdmin = async (user) => {
    try {
      const newRole = user.role === 'admin' ? 'user' : 'admin';
      await updateUserRole(user.id, newRole);
      toast.success(`${user.email} is now ${newRole === 'admin' ? 'an admin' : 'a regular user'}`);
      fetchUsers();
      setConfirmDialog({ show: false, type: null, user: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user role');
    }
  };

  const handleDeleteUser = async (user) => {
    try {
      const result = await deleteUser(user.id);
      toast.success(`User ${user.email} deleted successfully`);
      fetchUsers();
      setConfirmDialog({ show: false, type: null, user: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-amber-600" />
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900">Admin Panel</h1>
          </div>
          <p className="text-lg leading-relaxed text-slate-700">User account management and support</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2">
          <p className="text-sm font-semibold text-amber-900">Total Users: {total}</p>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold mb-1">Admin Access Notice</p>
            <p>You have admin-level access. All actions are logged for audit purposes. Admin users can manage account access but cannot view or modify clinical data (blood results, symptoms, notes).</p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <div className="grid md:grid-cols-3 gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              <Search className="inline h-4 w-4 mr-1" />
              Search by Name or Email
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Enter name or email..."
                className="flex-1 h-12 px-4 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
              <Button type="submit" className="btn-primary px-6">
                Search
              </Button>
            </div>
          </form>

          {/* Country Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              <Filter className="inline h-4 w-4 mr-1" />
              Filter by Country
            </label>
            <select
              value={countryFilter}
              onChange={(e) => {
                setCountryFilter(e.target.value);
                setPage(1);
              }}
              className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            >
              <option value="">All Countries</option>
              <option value="AU">Australia</option>
              <option value="US">United States</option>
            </select>
          </div>
        </div>

        {/* Sort */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">Sort By</label>
          <div className="flex gap-2">
            <button
              onClick={() => setSortBy('last_login')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                sortBy === 'last_login'
                  ? 'bg-primary text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Last Login
            </button>
            <button
              onClick={() => setSortBy('created_at')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                sortBy === 'created_at'
                  ? 'bg-primary text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Date Created
            </button>
            <button
              onClick={() => setSortBy('email')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                sortBy === 'email'
                  ? 'bg-primary text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Email
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto"></div>
              <p className="text-slate-600 mt-4">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 text-lg">No users found</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Name</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Email</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Country</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Plan</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">
                    {sortBy === 'created_at' ? 'Date Created' : 'Last Login'}
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{user.name}</span>
                        {user.role === 'admin' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            <Crown className="h-3 w-3" />
                            Admin
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-slate-700">{user.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-700">
                        {user.country === 'AU' ? 'Australia' : user.country === 'US' ? 'United States' : user.country}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.plan === 'paid' ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          <Sparkles className="h-3 w-3" />
                          Full Access
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          Free
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {user.account_status === 'locked' ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          <Lock className="h-3 w-3" />
                          Locked
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {sortBy === 'created_at' ? formatDate(user.created_at) : formatDate(user.last_login)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleViewAsUser(user)}
                          className="p-2 rounded-lg hover:bg-amber-50 text-amber-600 transition-colors"
                          title="View as User (Read-only)"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {user.account_status === 'locked' && (
                          <button
                            onClick={() => setConfirmDialog({ show: true, type: 'unlock', user })}
                            className="p-2 rounded-lg hover:bg-green-50 text-green-600 transition-colors"
                            title="Unlock Account"
                          >
                            <Lock className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setConfirmDialog({ show: true, type: 'reset', user })}
                          className="p-2 rounded-lg hover:bg-primary/10 text-primary transition-colors"
                          title="Send Password Reset"
                        >
                          <Key className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setConfirmDialog({ show: true, type: 'toggleAdmin', user })}
                          className={`p-2 rounded-lg transition-colors ${
                            user.role === 'admin' 
                              ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' 
                              : 'hover:bg-amber-50 text-slate-400 hover:text-amber-600'
                          }`}
                          title={user.role === 'admin' ? 'Remove Admin Access' : 'Make Admin'}
                        >
                          <Crown className="h-4 w-4" />
                        </button>
                        {user.plan !== 'paid' ? (
                          <button
                            onClick={() => setConfirmDialog({ show: true, type: 'upgrade', user })}
                            className="p-2 rounded-lg hover:bg-purple-50 text-purple-600 transition-colors"
                            title="Grant Full Access"
                          >
                            <Sparkles className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => setConfirmDialog({ show: true, type: 'downgrade', user })}
                            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
                            title="Remove Full Access"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setConfirmDialog({ show: true, type: 'delete', user })}
                          className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                          title="Delete User"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog.show && confirmDialog.user && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full" style={{ maxWidth: '360px' }}>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              {confirmDialog.type === 'unlock' && 'Unlock Account'}
              {confirmDialog.type === 'reset' && 'Reset Password'}
              {confirmDialog.type === 'upgrade' && 'Grant Full Access'}
              {confirmDialog.type === 'downgrade' && 'Remove Full Access'}
              {confirmDialog.type === 'toggleAdmin' && (confirmDialog.user.role === 'admin' ? 'Remove Admin Access' : 'Grant Admin Access')}
              {confirmDialog.type === 'delete' && '⚠️ Delete User'}
            </h3>
            
            <div className="mb-6">
              <div className="space-y-3">
                <p className="text-slate-700">
                  {confirmDialog.type === 'unlock' && 'Are you sure you want to unlock the account for:'}
                  {confirmDialog.type === 'reset' && 'Send a password reset email to:'}
                  {confirmDialog.type === 'upgrade' && 'Grant full access (unlimited tracking) to:'}
                  {confirmDialog.type === 'downgrade' && 'Remove full access from:'}
                  {confirmDialog.type === 'toggleAdmin' && (confirmDialog.user.role === 'admin' ? 'Remove admin access from:' : 'Grant admin access to:')}
                  {confirmDialog.type === 'delete' && 'Are you sure you want to permanently delete this user?'}
                </p>
                <div className={`rounded-lg p-3 ${confirmDialog.type === 'delete' ? 'bg-red-50 border border-red-200' : confirmDialog.type === 'toggleAdmin' ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
                  <p className="font-medium text-slate-900">{confirmDialog.user.name}</p>
                  <p className="text-sm text-slate-600">{confirmDialog.user.email}</p>
                  {confirmDialog.type === 'toggleAdmin' && (
                    <p className="text-xs text-amber-600 mt-1">Current role: {confirmDialog.user.role || 'user'}</p>
                  )}
                </div>
                {confirmDialog.type === 'delete' ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800 font-semibold mb-1">⚠️ This action cannot be undone!</p>
                    <p className="text-sm text-red-700">
                      This will permanently delete the user account and ALL associated data including:
                    </p>
                    <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                      <li>Blood results</li>
                      <li>Symptoms & mood logs</li>
                      <li>Medications & vitamins</li>
                      <li>Notes & ideas</li>
                      <li>All other user data</li>
                    </ul>
                  </div>
                ) : confirmDialog.type === 'toggleAdmin' ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      {confirmDialog.user.role === 'admin' 
                        ? 'This user will lose access to admin features including user management and the news crawler.'
                        : 'This user will get full admin access including user management, blog manager, and the news crawler.'}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600">
                    {confirmDialog.type === 'unlock' && 'This will reset the failed login attempts counter and allow the user to log in again.'}
                    {confirmDialog.type === 'reset' && 'The user will receive an email with a link to reset their password.'}
                    {confirmDialog.type === 'upgrade' && 'This user will get unlimited data entries and full access to all features.'}
                    {confirmDialog.type === 'downgrade' && 'This user will be limited to 3 data entries on the free plan.'}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setConfirmDialog({ show: false, type: null, user: null })}
                className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-900"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (confirmDialog.type === 'unlock') {
                    handleUnlockAccount(confirmDialog.user);
                  } else if (confirmDialog.type === 'reset') {
                    handleResetPassword(confirmDialog.user);
                  } else if (confirmDialog.type === 'upgrade') {
                    handleUpgradePlan(confirmDialog.user);
                  } else if (confirmDialog.type === 'downgrade') {
                    handleDowngradePlan(confirmDialog.user);
                  } else if (confirmDialog.type === 'toggleAdmin') {
                    handleToggleAdmin(confirmDialog.user);
                  } else if (confirmDialog.type === 'delete') {
                    handleDeleteUser(confirmDialog.user);
                  }
                }}
                className={`flex-1 ${
                  confirmDialog.type === 'delete' 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : confirmDialog.type === 'toggleAdmin'
                      ? 'bg-amber-500 hover:bg-amber-600 text-white'
                      : 'btn-primary'
                }`}
              >
                {confirmDialog.type === 'delete' ? 'Delete Permanently' : confirmDialog.type === 'toggleAdmin' ? (confirmDialog.user?.role === 'admin' ? 'Remove Admin' : 'Make Admin') : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
